import {
    computed,
    inject,
    Injectable,
    signal
} from '@angular/core';

import {
    finalize,
    Observable,
    tap,
    throwError
} from 'rxjs';

import {
    MyRestaurantResponse
} from '../models/my-restaurant-response';

import {
    BranchResponse
} from '../models/branch-response';

import {
    CreateBranchRequest
} from '../models/create-branch-request';

import {
    RestaurantService
} from './restaurant.service';

import {
    BranchService
} from './branch.service';
import {
    UpdateBranchRequest
} from '../models/update-branch-request';
import {
    UpdateBranchStatusRequest
} from '../models/update-branch-status-request';

@Injectable({
    providedIn: 'root'
})
export class RestaurantContextService {

    /*
     * =========================================================
     * SERVICES
     * =========================================================
     */

    private readonly restaurantService =
        inject(RestaurantService);

    private readonly branchService =
        inject(BranchService);


    /*
     * =========================================================
     * STORAGE KEYS
     * =========================================================
     */

    private readonly SELECTED_RESTAURANT_KEY =
        'restaurantos_selected_restaurant_id';

    private readonly SELECTED_BRANCH_KEY =
        'restaurantos_selected_branch_id';


    /*
     * =========================================================
     * RESTAURANT STATE
     * =========================================================
     */

    private readonly restaurantsSignal =
        signal<MyRestaurantResponse[]>([]);


    private readonly selectedRestaurantSignal =
        signal<MyRestaurantResponse | null>(
            null
        );


    /*
     * =========================================================
     * BRANCH STATE
     * =========================================================
     */

    private readonly branchesSignal =
        signal<BranchResponse[]>([]);


    private readonly selectedBranchSignal =
        signal<BranchResponse | null>(
            null
        );


    /*
     * Nos permite saber cuál fue la última
     * solicitud de Branches realizada.
     *
     * Esto evita problemas si el usuario cambia
     * rápidamente de Restaurant.
     */
    private branchRequestSequence = 0;


    /*
     * =========================================================
     * LOADING / ERROR STATE
     * =========================================================
     */

    private readonly loadingSignal =
        signal(false);


    private readonly branchesLoadingSignal =
        signal(false);


    private readonly errorSignal =
        signal<string | null>(
            null
        );


    /*
     * =========================================================
     * PUBLIC RESTAURANT SIGNALS
     * =========================================================
     */

    readonly restaurants =
        this.restaurantsSignal
            .asReadonly();


    readonly selectedRestaurant =
        this.selectedRestaurantSignal
            .asReadonly();


    readonly selectedRestaurantId =
        computed(() =>
            this.selectedRestaurantSignal()
                ?.id ?? null
        );


    readonly hasRestaurants =
        computed(() =>
            this.restaurantsSignal()
                .length > 0
        );


    /*
     * =========================================================
     * PUBLIC BRANCH SIGNALS
     * =========================================================
     */

    readonly branches =
        this.branchesSignal
            .asReadonly();


    readonly selectedBranch =
        this.selectedBranchSignal
            .asReadonly();


    readonly selectedBranchId =
        computed(() =>
            this.selectedBranchSignal()
                ?.id ?? null
        );


    readonly hasBranches =
        computed(() =>
            this.branchesSignal()
                .length > 0
        );


    /*
     * =========================================================
     * PUBLIC GENERAL SIGNALS
     * =========================================================
     */

    readonly loading =
        this.loadingSignal
            .asReadonly();


    readonly branchesLoading =
        this.branchesLoadingSignal
            .asReadonly();


    readonly error =
        this.errorSignal
            .asReadonly();


    /*
     * =========================================================
     * LOAD RESTAURANTS
     * =========================================================
     */

    loadRestaurants(): void {

        /*
         * Evitamos llamadas duplicadas.
         */
        if (
            this.loadingSignal()
        ) {

            return;
        }


        /*
         * Limpiamos estado anterior.
         */
        this.restaurantsSignal.set(
            []
        );


        this.selectedRestaurantSignal.set(
            null
        );


        this.branchesSignal.set(
            []
        );


        this.selectedBranchSignal.set(
            null
        );


        this.errorSignal.set(
            null
        );


        this.loadingSignal.set(
            true
        );


        this.restaurantService
            .getMyRestaurants()
            .pipe(

                finalize(() => {

                    this.loadingSignal.set(
                        false
                    );

                })

            )
            .subscribe({

                next: (
                    restaurants:
                        MyRestaurantResponse[]
                ) => {

                    this.restaurantsSignal.set(
                        restaurants
                    );


                    /*
                     * Restauramos Restaurant anterior
                     * o seleccionamos el primero.
                     */
                    this.restoreRestaurantSelection(
                        restaurants
                    );

                },


                error: () => {

                    this.errorSignal.set(
                        'No se pudieron cargar los restaurantes.'
                    );

                }

            });
    }


    /*
     * =========================================================
     * SELECT RESTAURANT
     * =========================================================
     */

    selectRestaurant(
        restaurant:
            MyRestaurantResponse
    ): void {

        const currentRestaurant =
            this.selectedRestaurantSignal();


        /*
         * Si ya es el Restaurant actual,
         * no hacemos nada.
         */
        if (
            currentRestaurant?.id ===
            restaurant.id
        ) {

            return;
        }


        /*
         * Guardamos Restaurant actual.
         */
        this.selectedRestaurantSignal
            .set(
                restaurant
            );


        localStorage.setItem(
            this.SELECTED_RESTAURANT_KEY,
            restaurant.id
        );


        /*
         * Cuando cambia Restaurant,
         * la Branch anterior ya no es válida.
         */
        this.branchesSignal.set(
            []
        );


        this.selectedBranchSignal.set(
            null
        );


        localStorage.removeItem(
            this.SELECTED_BRANCH_KEY
        );


        /*
         * Cargamos las Branches
         * del nuevo Restaurant.
         */
        this.loadBranches(
            restaurant.id
        );
    }


    /*
     * =========================================================
     * LOAD BRANCHES
     * =========================================================
     */

    loadBranches(
        restaurantId?: string
    ): void {

        const targetRestaurantId =
            restaurantId
            ??
            this.selectedRestaurantId();


        /*
         * No existe Restaurant seleccionado.
         */
        if (
            targetRestaurantId === null
        ) {

            this.branchesSignal.set(
                []
            );


            this.selectedBranchSignal.set(
                null
            );


            return;
        }


        /*
         * Cada llamada obtiene un número.
         *
         * Si después llega otra llamada,
         * la respuesta anterior será ignorada.
         */
        const requestSequence =
            ++this.branchRequestSequence;


        this.errorSignal.set(
            null
        );


        this.branchesLoadingSignal.set(
            true
        );


        this.branchService
            .getBranches(
                targetRestaurantId
            )
            .pipe(

                finalize(() => {

                    /*
                     * Solamente la petición más reciente
                     * puede quitar el loading.
                     */
                    if (
                        requestSequence ===
                        this.branchRequestSequence
                    ) {

                        this.branchesLoadingSignal
                            .set(
                                false
                            );

                    }

                })

            )
            .subscribe({

                next: (
                    branches:
                        BranchResponse[]
                ) => {

                    /*
                     * Si esta petición ya no es
                     * la última, descartamos respuesta.
                     */
                    if (
                        requestSequence !==
                        this.branchRequestSequence
                    ) {

                        return;
                    }


                    /*
                     * Protección adicional:
                     *
                     * el Restaurant debe seguir siendo
                     * el mismo que originó la petición.
                     */
                    if (
                        this.selectedRestaurantId()
                        !==
                        targetRestaurantId
                    ) {

                        return;
                    }


                    this.branchesSignal.set(
                        branches
                    );


                    this.restoreBranchSelection(
                        branches
                    );

                },


                error: () => {

                    /*
                     * Ignoramos errores de requests
                     * antiguos.
                     */
                    if (
                        requestSequence !==
                        this.branchRequestSequence
                    ) {

                        return;
                    }


                    this.branchesSignal.set(
                        []
                    );


                    this.selectedBranchSignal.set(
                        null
                    );


                    this.errorSignal.set(
                        'No se pudieron cargar las sucursales.'
                    );

                }

            });
    }


    /*
     * =========================================================
     * CREATE BRANCH
     * =========================================================
     */

    createBranch(
        request:
            CreateBranchRequest
    ): Observable<BranchResponse> {

        const restaurantId =
            this.selectedRestaurantId();


        /*
         * No se puede crear una Branch
         * si no existe Restaurant seleccionado.
         */
        if (
            restaurantId === null
        ) {

            return throwError(
                () =>
                    new Error(
                        'No restaurant selected'
                    )
            );
        }


        /*
         * Llamamos al POST del BranchService.
         */
        return this.branchService
            .createBranch(
                restaurantId,
                request
            )
            .pipe(

                tap(
                    createdBranch => {

                        /*
                         * Puede ocurrir que mientras
                         * se procesaba el POST,
                         * el usuario cambie de Restaurant.
                         *
                         * En ese caso no agregamos la Branch
                         * al contexto equivocado.
                         */
                        if (
                            this.selectedRestaurantId()
                            !==
                            restaurantId
                        ) {

                            return;
                        }


                        /*
                         * Agregamos la Branch creada
                         * directamente al Signal.
                         *
                         * No hacemos otro GET.
                         */
                        this.branchesSignal.update(
                            currentBranches => [

                                ...currentBranches,

                                createdBranch
                            ]
                        );


                        /*
                         * Seleccionamos automáticamente
                         * la nueva Branch.
                         */
                        this.selectBranch(
                            createdBranch
                        );

                    }
                )

            );
    }

    /*
 * =========================================================
 * UPDATE BRANCH
 * =========================================================
 */

updateBranch(
    branchId: string,
    request: UpdateBranchRequest
): Observable<BranchResponse> {

    const restaurantId =
        this.selectedRestaurantId();


    /*
     * Debe existir Restaurant seleccionado.
     */
    if (
        restaurantId === null
    ) {

        return throwError(
            () =>
                new Error(
                    'No restaurant selected'
                )
        );
    }


    /*
     * Verificamos que la Branch que queremos
     * actualizar pertenezca al contexto actual.
     */
    const currentBranch =
        this.branchesSignal()
            .find(
                branch =>
                    branch.id === branchId
            );


    if (
        !currentBranch ||
        currentBranch.restaurantId !== restaurantId
    ) {

        return throwError(
            () =>
                new Error(
                    'Branch does not belong to selected restaurant'
                )
        );
    }


    return this.branchService
        .updateBranch(
            restaurantId,
            branchId,
            request
        )
        .pipe(

            tap(
                updatedBranch => {

                    /*
                     * Si el usuario cambió de Restaurant
                     * mientras se ejecutaba el PUT,
                     * descartamos la actualización visual.
                     */
                    if (
                        this.selectedRestaurantId()
                        !==
                        restaurantId
                    ) {

                        return;
                    }


                    /*
                     * Reemplazamos únicamente la Branch
                     * actualizada.
                     *
                     * NO hacemos otro GET.
                     */
                    this.branchesSignal.update(
                        currentBranches =>
                            currentBranches.map(
                                branch =>
                                    branch.id ===
                                    updatedBranch.id
                                        ? updatedBranch
                                        : branch
                            )
                    );


                    /*
                     * Si la Branch modificada era también
                     * la Branch seleccionada, actualizamos
                     * selectedBranch.
                     *
                     * Así cambia automáticamente el Sidebar.
                     */
                    if (
                        this.selectedBranchId()
                        ===
                        updatedBranch.id
                    ) {

                        this.selectedBranchSignal
                            .set(
                                updatedBranch
                            );

                    }

                }
            )

        );
}

/*
 * =========================================================
 * UPDATE BRANCH STATUS
 * =========================================================
 */

updateBranchStatus(
    branchId: string,
    status: 'ACTIVE' | 'INACTIVE'
): Observable<BranchResponse> {

    const restaurantId =
        this.selectedRestaurantId();


    if (
        restaurantId === null
    ) {

        return throwError(
            () =>
                new Error(
                    'No restaurant selected'
                )
        );
    }


    const currentBranch =
        this.branchesSignal()
            .find(
                branch =>
                    branch.id === branchId
            );


    if (
        !currentBranch ||
        currentBranch.restaurantId !== restaurantId
    ) {

        return throwError(
            () =>
                new Error(
                    'Branch does not belong to selected restaurant'
                )
        );
    }


    const request:
        UpdateBranchStatusRequest = {

        status

    };


    return this.branchService
        .updateBranchStatus(
            restaurantId,
            branchId,
            request
        )
        .pipe(

            tap(
                updatedBranch => {

                    /*
                     * Si durante el PATCH se cambió
                     * de restaurante, no modificamos
                     * el contexto visual actual.
                     */
                    if (
                        this.selectedRestaurantId()
                        !==
                        restaurantId
                    ) {

                        return;
                    }


                    /*
                     * Reemplazamos únicamente la
                     * sucursal actualizada.
                     */
                    this.branchesSignal.update(
                        currentBranches =>
                            currentBranches.map(
                                branch =>
                                    branch.id ===
                                    updatedBranch.id
                                        ? updatedBranch
                                        : branch
                            )
                    );


                    /*
                     * Si era la sucursal seleccionada,
                     * actualizamos también ese Signal.
                     *
                     * No la deseleccionamos por estar
                     * INACTIVE; simplemente reflejamos
                     * su nuevo estado.
                     */
                    if (
                        this.selectedBranchId()
                        ===
                        updatedBranch.id
                    ) {

                        this.selectedBranchSignal
                            .set(
                                updatedBranch
                            );

                    }

                }
            )

        );
}

    /*
     * =========================================================
     * SELECT BRANCH
     * =========================================================
     */

    selectBranch(
        branch:
            BranchResponse
    ): void {

        /*
         * La Branch seleccionada debe pertenecer
         * al Restaurant actualmente seleccionado.
         */
        if (
            branch.restaurantId !==
            this.selectedRestaurantId()
        ) {

            return;
        }


        this.selectedBranchSignal
            .set(
                branch
            );


        localStorage.setItem(
            this.SELECTED_BRANCH_KEY,
            branch.id
        );
    }


    /*
     * =========================================================
     * CLEAR
     * =========================================================
     */

    clear(): void {

        /*
         * Invalidamos cualquier request
         * de Branch que siga pendiente.
         */
        this.branchRequestSequence++;


        this.restaurantsSignal.set(
            []
        );


        this.selectedRestaurantSignal.set(
            null
        );


        this.branchesSignal.set(
            []
        );


        this.selectedBranchSignal.set(
            null
        );


        this.errorSignal.set(
            null
        );


        this.loadingSignal.set(
            false
        );


        this.branchesLoadingSignal.set(
            false
        );
    }


    /*
     * =========================================================
     * RESTORE RESTAURANT SELECTION
     * =========================================================
     */

    private restoreRestaurantSelection(
        restaurants:
            MyRestaurantResponse[]
    ): void {

        /*
         * Usuario sin Restaurants.
         */
        if (
            restaurants.length === 0
        ) {

            this.selectedRestaurantSignal
                .set(
                    null
                );


            this.branchesSignal.set(
                []
            );


            this.selectedBranchSignal.set(
                null
            );


            localStorage.removeItem(
                this.SELECTED_RESTAURANT_KEY
            );


            localStorage.removeItem(
                this.SELECTED_BRANCH_KEY
            );


            return;
        }


        /*
         * Buscamos Restaurant guardado.
         */
        const savedRestaurantId =
            localStorage.getItem(
                this.SELECTED_RESTAURANT_KEY
            );


        if (
            savedRestaurantId
        ) {

            const savedRestaurant =
                restaurants.find(
                    restaurant =>
                        restaurant.id ===
                        savedRestaurantId
                );


            /*
             * El Restaurant guardado
             * todavía pertenece al usuario.
             */
            if (
                savedRestaurant
            ) {

                this.selectedRestaurantSignal
                    .set(
                        savedRestaurant
                    );


                this.loadBranches(
                    savedRestaurant.id
                );


                return;
            }
        }


        /*
         * Si no existe selección anterior,
         * seleccionamos el primer Restaurant.
         */
        const firstRestaurant =
            restaurants[0];


        /*
         * Protección para TypeScript strict
         * y noUncheckedIndexedAccess.
         */
        if (
            !firstRestaurant
        ) {

            return;
        }


        this.selectedRestaurantSignal
            .set(
                firstRestaurant
            );


        localStorage.setItem(
            this.SELECTED_RESTAURANT_KEY,
            firstRestaurant.id
        );


        this.loadBranches(
            firstRestaurant.id
        );
    }


    /*
     * =========================================================
     * RESTORE BRANCH SELECTION
     * =========================================================
     */

    private restoreBranchSelection(
        branches:
            BranchResponse[]
    ): void {

        /*
         * Restaurant sin Branches.
         */
        if (
            branches.length === 0
        ) {

            this.selectedBranchSignal
                .set(
                    null
                );


            localStorage.removeItem(
                this.SELECTED_BRANCH_KEY
            );


            return;
        }


        /*
         * Buscamos Branch guardada.
         */
        const savedBranchId =
            localStorage.getItem(
                this.SELECTED_BRANCH_KEY
            );


        if (
            savedBranchId
        ) {

            const savedBranch =
                branches.find(
                    branch =>
                        branch.id ===
                        savedBranchId
                );


            /*
             * La Branch guardada
             * todavía existe.
             */
            if (
                savedBranch
            ) {

                this.selectedBranchSignal
                    .set(
                        savedBranch
                    );


                return;
            }
        }


        /*
         * Si no existe selección anterior,
         * seleccionamos la primera Branch.
         */
        const firstBranch =
            branches[0];


        /*
         * Protección TypeScript strict.
         */
        if (
            !firstBranch
        ) {

            return;
        }


        this.selectedBranchSignal
            .set(
                firstBranch
            );


        localStorage.setItem(
            this.SELECTED_BRANCH_KEY,
            firstBranch.id
        );
    }
}